import type { useChat } from '@ai-sdk/react';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Icon from '@cobliteam/ui/icons';
import { type FormEvent, useState } from 'react';

type ChatStatus = ReturnType<typeof useChat>['status'];
type SendMessage = ReturnType<typeof useChat>['sendMessage'];

interface FloatingAIChatInputProps {
  sendMessage: SendMessage;
  status: ChatStatus;
  stop: () => void;
}

const FloatingAIChatInput = ({ sendMessage, status, stop }: FloatingAIChatInputProps) => {
  const { t } = useTranslation('ai');
  const [text, setText] = useState('');
  const isStreaming = status === 'streaming' || status === 'submitted';

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    void sendMessage({
      role: 'user',
      id: crypto.randomUUID(),
      parts: [{ type: 'text', text: value }],
    });
    setText('');
  };

  return (
    <form onSubmit={handleSubmit} className='flex items-center gap-2 border-gray-4 border-t bg-white px-3 py-2'>
      <input
        type='text'
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t('prompt_placeholder')}
        disabled={isStreaming}
        className='flex-1 rounded-md border border-gray-4 px-3 py-2 text-sm focus:border-primary-9 focus:outline-none disabled:bg-gray-2'
        data-testid='floating-ai-chat-input'
      />
      {isStreaming ? (
        <Button type='button' variant='contained' color='primary' size='sm' icon={<Icon name='square' size='sm' />} onClick={stop} aria-label={t('stop')} />
      ) : (
        <Button type='submit' variant='contained' color='primary' size='sm' icon={<Icon name='arrow-up' size='sm' />} disabled={!text.trim()} aria-label={t('send')} />
      )}
    </form>
  );
};

export default FloatingAIChatInput;
